import { useMemo, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router';
import BrandLogo from '../components/BrandLogo';
import PrimaryButton from '../components/PrimaryButton';
import {
  mapVerificarOTPError,
  verificarOTP,
} from '../services/authService';
import { useAuthStore } from '../store/authStore';

function validateOTP(otp) {
  if (!otp) {
    return 'El código es requerido';
  }

  if (!/^\d+$/.test(otp)) {
    return 'El código solo puede contener números';
  }

  if (otp.length !== 6) {
    return 'El código debe tener exactamente 6 dígitos';
  }

  return '';
}

function VerificarOTPPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const email = location.state?.email;
  const setSession = useAuthStore((state) => state.setSession);
  const setLoading = useAuthStore((state) => state.setLoading);
  const setStoreError = useAuthStore((state) => state.setError);
  const isLoading = useAuthStore((state) => state.isLoading);
  const [otp, setOtp] = useState('');
  const [touched, setTouched] = useState(false);
  const [apiError, setApiError] = useState('');
  const [canRequestNewCode, setCanRequestNewCode] = useState(false);
  const validationError = useMemo(() => validateOTP(otp), [otp]);
  const visibleError = touched ? validationError || apiError : apiError;
  const isDisabled = isLoading || Boolean(validationError);

  if (!email) {
    return <Navigate to="/login" replace />;
  }

  const handleChange = (event) => {
    const nextValue = event.target.value.replace(/\D/g, '').slice(0, 6);
    setOtp(nextValue);
    setApiError('');
    setCanRequestNewCode(false);
    setStoreError(null);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setTouched(true);
    setApiError('');
    setCanRequestNewCode(false);
    setStoreError(null);

    if (validationError) {
      return;
    }

    setLoading(true);

    try {
      const responseData = await verificarOTP({ email, otp });

      if (!responseData?.data?.token || !responseData?.data?.usuario) {
        throw new Error('Respuesta del servidor inválida');
      }

      const session = {
        accessToken: responseData.data.token,
        user: responseData.data.usuario,
      };

      setSession(session);
      navigate('/dashboard', { replace: true });
    } catch (error) {
      const nextError = mapVerificarOTPError(error);
      setApiError(nextError.message);
      setCanRequestNewCode(nextError.canRequestNewCode);
      setStoreError(nextError.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="flex min-h-screen items-center px-4 py-6 sm:px-6 lg:px-8">
      <div className="mx-auto grid w-full max-w-[1100px] items-center gap-10 lg:grid-cols-[minmax(0,1fr)_420px]">
        <div className="hidden lg:block">
          <div className="max-w-[420px]">
            <p className="font-login text-[13px] font-bold uppercase tracking-[0.26em] text-[#8390a3]">
              Eva Ops
            </p>
            <h1 className="mt-4 font-login text-[42px] font-bold leading-[1.05] text-[#111111]">
              Verificación de acceso.
            </h1>
            <p className="mt-5 max-w-[360px] font-login text-[16px] leading-7 text-[#5f6673]">
              Escribí el código de seis dígitos que enviamos a {email}.
            </p>
          </div>
        </div>

        <div className="w-full rounded-[24px] border border-[#d7dce2] bg-white px-5 py-6 shadow-[0_22px_65px_rgba(15,23,42,0.12)] sm:mx-auto sm:max-w-[420px] sm:px-7 sm:py-8">
          <div className="border-b border-[#edf0f4] pb-5">
            <BrandLogo />
          </div>

          <div className="pt-10 sm:pt-12">
            <form className="space-y-3" onSubmit={handleSubmit} noValidate>
              <label htmlFor="otp" className="sr-only">
                Código OTP
              </label>
              <div className="flex h-11 items-center overflow-hidden rounded-[4px] border border-eva-border bg-[#dfe8f5] shadow-field transition focus-within:border-[#b9c7db]">
                <span className="flex h-full w-11 items-center justify-center border-r border-[#c7d1e0] bg-[#eef2f7] font-login text-[13px] font-bold text-eva-icon">
                  OTP
                </span>
                <input
                  id="otp"
                  type="text"
                  name="otp"
                  value={otp}
                  onChange={handleChange}
                  onBlur={() => setTouched(true)}
                  placeholder="123456"
                  autoComplete="one-time-code"
                  inputMode="numeric"
                  maxLength={6}
                  pattern="[0-9]*"
                  className="h-full flex-1 border-0 bg-transparent px-3 font-login text-[14px] text-[#0f172a] outline-none placeholder:text-[#6b7280]"
                />
              </div>

              {visibleError ? (
                <p className="pt-1 font-login text-[13px] text-[#c43c2f]">
                  {visibleError}
                </p>
              ) : null}

              {canRequestNewCode ? (
                <button
                  type="button"
                  onClick={() => navigate('/login')}
                  className="font-login text-[13px] font-bold text-[#2e86b5] underline-offset-4 transition hover:underline"
                >
                  Solicitar un nuevo código
                </button>
              ) : null}

              <div className="pt-5">
                <PrimaryButton type="submit" disabled={isDisabled}>
                  {isLoading ? 'Verificando...' : 'Ingresar'}
                </PrimaryButton>
              </div>

              <button
                type="button"
                onClick={() => navigate('/login')}
                className="w-full pt-2 text-center font-login text-[14px] font-bold text-[#5f6673] transition hover:text-[#111111]"
              >
                Volver
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}

export default VerificarOTPPage;
